import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  SimpleGrid,
  Badge,
  Center,
  Image,
  Radio,
  Divider,
  Modal
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconCrown
} from '@tabler/icons-react';

const PricingCard = ({ title, price, period, features, recommended = false, onSelect }) => {
  return (
    <Card 
      shadow="lg" 
      padding="xl" 
      radius="md" 
      withBorder
      style={{ 
        border: recommended ? '2px solid #9f38cc' : '1px solid #dee2e6',
        position: 'relative'
      }}
    >
      {recommended && (
        <Badge 
          color="grape" 
          style={{ 
            position: 'absolute', 
            top: -12, 
            left: '50%', 
            transform: 'translateX(-50%)' 
          }}
        >
          RECOMMENDED
        </Badge>
      )}
      <Stack gap="md" align="center">
        <Title order={3}>{title}</Title>
        <Text size="xl" fw={700}>
          ${price}
          <Text span size="sm" c="dimmed">/{period}</Text>
        </Text>
        <Divider my="sm" />
        <Stack gap="xs" style={{ width: '100%' }}>
          {features.map((feature, index) => (
            <Group key={index} gap="xs" align="flex-start">
              <IconCheck size="1rem" color="var(--mantine-color-green-6)" style={{ marginTop: 2 }} />
              <Text size="sm">{feature}</Text>
            </Group>
          ))}
        </Stack>
        <Button 
          fullWidth 
          mt="md" 
          variant={recommended ? 'filled' : 'outline'}
          color="grape"
          onClick={onSelect}
        >
          Select {period === 'month' ? 'Monthly' : 'Yearly'}
        </Button>
      </Stack>
    </Card>
  );
};

const SubscriptionPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [modalOpened, setModalOpened] = useState(false);

  const basicFeatures = [
    "Pedi Piggy markets your account for you",
    "Profile visible to all Pedi Piggy's",
    "Custom content blurring, account blocking",
    "Premium customer support",
    "85% payout on all sales",
    "Upload unlimited photos to your album"
  ];

  const premiumFeatures = [
    "Includes all Basic Princess subscription features",
    "Profile appears before all Basic Princess users",
    "Unlimited Spoiling and uploads",
    "Custom content blurring, account blocking",
    "10 free winks per month",
    "90% payouts on all sales"
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Button 
            component={Link} 
            to="/" 
            variant="subtle" 
            leftSection={<IconArrowLeft size="1rem" />}
          >
            Back to Home
          </Button>
        </Group>

        <Center>
          <Stack gap="md" align="center" maw={600}>
            <IconCrown size={48} color="var(--mantine-color-grape-6)" />
            <Title order={1} ta="center">
              Subscribe to have your pedicures paid for
            </Title>
            <Text size="lg" ta="center" c="dimmed">
              Choose the plan that works for you and start earning today!
            </Text>
          </Stack>
        </Center>

        <Center>
          <Radio.Group 
            value={billingPeriod} 
            onChange={setBillingPeriod}
            withAsterisk
          >
            <Group gap="xl">
              <Radio 
                value="monthly" 
                label="Monthly" 
                size="lg"
                styles={{ label: { fontSize: '1.1rem' } }}
              />
              <Radio 
                value="yearly" 
                label="Yearly" 
                size="lg"
                styles={{ label: { fontSize: '1.1rem' } }}
              />
            </Group>
          </Radio.Group>
        </Center>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" maw={900} mx="auto">
          <PricingCard 
            title="Basic Princess"
            price={billingPeriod === 'monthly' ? '5.99' : '29.99'}
            period={billingPeriod === 'monthly' ? 'month' : 'year'}
            features={basicFeatures}
            onSelect={() => setModalOpened(true)}
          />
          <PricingCard 
            title="Spoiled Princess"
            price={billingPeriod === 'monthly' ? '12.99' : '49.99'}
            period={billingPeriod === 'monthly' ? 'month' : 'year'}
            features={premiumFeatures}
            recommended={true}
            onSelect={() => setModalOpened(true)}
          />
        </SimpleGrid>

        <Text size="sm" c="dimmed" ta="center">
          By subscribing, you agree to our Terms of Service and Privacy Policy.
          You can cancel anytime.
        </Text>

        <Modal 
          opened={modalOpened} 
          onClose={() => setModalOpened(false)}
          title="Ready to Join?"
          centered
        >
          <Stack gap="md">
            <Text ta="center">
              Select an option to continue:
            </Text>
            <Button 
              component={Link} 
              to="/register" 
              size="lg" 
              color="grape"
              fullWidth
            >
              Create New Account
            </Button>
            <Button 
              component={Link} 
              to="/login" 
              size="lg" 
              variant="outline"
              color="grape"
              fullWidth
            >
              Log in Here
            </Button>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
};

export default SubscriptionPage;